// ============================================================================
// Claude Code Redux Slice
//
// Manages: connection status, session ID, server URL, model, streaming state.
// Conversation messages are managed via the existing chatGPT reducer.
// Server URL and model are persisted to localStorage.
// ============================================================================
import { createSlice } from "@reduxjs/toolkit";
// ---------------------------------------------------------------------------
// Defaults & localStorage persistence
// ---------------------------------------------------------------------------
const STORAGE_KEY_URL = "claudecode_server_url";
const STORAGE_KEY_MODEL = "claudecode_model";
const STORAGE_KEY_API_KEY = "claudecode_api_key";
const DEFAULT_SERVER_URL = "ws://localhost:3001";
const DEFAULT_MODEL = "claude-sonnet-4-6";
const DEFAULT_API_KEY = "";
/** Available models for the dropdown */
export const CLAUDE_CODE_MODELS = [
    { value: "claude-opus-4-6", label: "Claude Opus 4.6" },
    { value: "claude-sonnet-4-6", label: "Claude Sonnet 4.6" },
    { value: "claude-haiku-4-5-20251001", label: "Claude Haiku 4.5" }
];
function loadFromStorage(key, fallback) {
    try {
        return localStorage.getItem(key) ?? fallback;
    }
    catch {
        return fallback;
    }
}
function saveToStorage(key, value) {
    try {
        localStorage.setItem(key, value);
    }
    catch { }
}
const initialState = {
    connectionStatus: "disconnected",
    sessionId: `aible-${crypto.randomUUID().slice(0, 8)}`,
    serverUrl: loadFromStorage(STORAGE_KEY_URL, DEFAULT_SERVER_URL),
    model: loadFromStorage(STORAGE_KEY_MODEL, DEFAULT_MODEL),
    apiKey: loadFromStorage(STORAGE_KEY_API_KEY, DEFAULT_API_KEY),
    streamingMessageKey: null,
    currentToolCall: null,
    error: null
};
const claudeCodeSlice = createSlice({
    name: "claudeCode",
    initialState,
    reducers: {
        setConnectionStatus(state, action) {
            state.connectionStatus = action.payload;
            if (action.payload === "connected") {
                state.error = null;
            }
        },
        setServerUrl(state, action) {
            state.serverUrl = action.payload;
            saveToStorage(STORAGE_KEY_URL, action.payload);
        },
        setModel(state, action) {
            state.model = action.payload;
            saveToStorage(STORAGE_KEY_MODEL, action.payload);
        },
        setApiKey(state, action) {
            state.apiKey = action.payload;
            saveToStorage(STORAGE_KEY_API_KEY, action.payload);
        },
        setSessionId(state, action) {
            state.sessionId = action.payload;
        },
        setStreamingMessageKey(state, action) {
            state.streamingMessageKey = action.payload;
        },
        setCurrentToolCall(state, action) {
            state.currentToolCall = action.payload;
        },
        setClaudeCodeError(state, action) {
            state.error = action.payload;
        },
        resetClaudeCodeState() {
            return { ...initialState, sessionId: `aible-${crypto.randomUUID().slice(0, 8)}` };
        }
    }
});
export const { setConnectionStatus, setServerUrl, setModel, setApiKey, setSessionId, setStreamingMessageKey, setCurrentToolCall, setClaudeCodeError, resetClaudeCodeState } = claudeCodeSlice.actions;
export default claudeCodeSlice.reducer;
export const selectClaudeCodeConnectionStatus = (state) => state.claudeCode?.connectionStatus ?? "disconnected";
export const selectClaudeCodeSessionId = (state) => state.claudeCode?.sessionId ?? "";
export const selectClaudeCodeStreamingMessageKey = (state) => state.claudeCode?.streamingMessageKey ?? null;
export const selectClaudeCodeError = (state) => state.claudeCode?.error ?? null;
export const selectCurrentToolCall = (state) => state.claudeCode?.currentToolCall ?? null;
export const selectClaudeCodeServerUrl = (state) => state.claudeCode?.serverUrl ?? DEFAULT_SERVER_URL;
export const selectClaudeCodeModel = (state) => state.claudeCode?.model ?? DEFAULT_MODEL;
export const selectClaudeCodeApiKey = (state) => state.claudeCode?.apiKey ?? "";
export const selectIsClaudeCodeConnected = (state) => state.claudeCode?.connectionStatus === "connected";
